import { dom } from "@fortawesome/fontawesome-svg-core";
import { EventSourcePolyfill } from "event-source-polyfill";
import { jwtDecode } from "jwt-decode";
import { formatMessageTimes } from "../../utils/time-format";
import { announceReceivedMessage } from "./chat-announcer";
import { MercureFetchTransport } from "./mercure-fetch-transport";

export const initConversationMessaging = () => {
  const conversation = document.getElementById("conversation") as HTMLDivElement;

  if (!conversation) {
    return;
  }

  const messageListsScrollContent = conversation.querySelector(
    ".message-list .scroll-content",
  ) as HTMLDivElement;

  if (!messageListsScrollContent) {
    return;
  }

  const submitMessageForm = document.getElementById("submit-message-form") as HTMLFormElement;
  const textarea = submitMessageForm.querySelector("textarea") as HTMLTextAreaElement;
  const messagesWrapper = document.querySelector(".messages-wrapper") as HTMLDivElement;
  const scrollElement = document.querySelector(".scroll-content") as HTMLDivElement;

  let isAbortedConnection = false;
  let reconnectInterval: number | undefined;
  let evtSource: EventSourcePolyfill;

  const subscribeURL = new URL(window.APP_PROPS.mercure_public_url);
  const subscribeToken = window.APP_PROPS.subscribe_key;

  // Mercure 1.0 exact-match subscription parameter (was `topic` in 0.x).
  subscribeURL.searchParams.append(
    "match",
    submitMessageForm.dataset.subscribeTopic ? submitMessageForm.dataset.subscribeTopic : "",
  );

  // Restore user typed text if the page has been reloaded.
  if (localStorage.messageContent) {
    textarea.value = window.localStorage.messageContent;
    localStorage.removeItem("messageContent");
  }

  // Start connection.
  subscribeToTopic();

  function subscribeToTopic() {
    evtSource = new EventSourcePolyfill(subscribeURL.toString(), {
      headers: {
        Authorization: `Bearer ${subscribeToken}`,
      },
      // The polyfill's own fetch transport leaves reader.cancel() unhandled when close() runs
      // after the stream errored, which is exactly what onerror below does — see the class doc.
      Transport: MercureFetchTransport,
    });

    evtSource.onopen = () => {
      if (isAbortedConnection) {
        // Save user text before the page is reloaded.
        localStorage.messageContent = textarea.value;

        location.reload();
      }
    };

    evtSource.onmessage = ({ data }: { data: string }) => {
      const parsed = JSON.parse(data);

      setTimeout(() => {
        // Check if Ajax already delivered this message
        if (hasAlreadyMessage(parsed.message_id)) {
          return;
        }

        fetchAndRenderMessages(parsed.message_id);
      }, 3000);
    };

    evtSource.onerror = () => {
      evtSource.close();

      const { exp }: { exp: number } = jwtDecode(subscribeToken);
      const currentTime = Date.now() / 1000;

      //Token is expired
      if (currentTime > exp) {
        // Save user text before the page is reloaded, as the reconnect path in onopen does.
        localStorage.messageContent = textarea.value;

        location.reload();

        return;
      }

      //Start reconnect attempts
      startResubscribing();
    };
  }

  function startResubscribing() {
    isAbortedConnection = true;

    // One interval for the page's lifetime: every disconnect used to add another one.
    if (reconnectInterval !== undefined) {
      return;
    }

    reconnectInterval = window.setInterval(() => {
      if (evtSource.readyState === EventSource.CLOSED) {
        console.log("Reconnecting...");

        subscribeToTopic();
      }
    }, 60000); // Try to reconnect after every 60 seconds.
  }

  function fetchAndRenderMessages(messageId: number) {
    fetch(`/website-api/inbox/message/${messageId}/`, {
      credentials: "same-origin",
      method: "get",
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.metadata.code !== 200) {
          // Target language invalid
          alert(json.metadata.error_message);
          return;
        }

        if (hasAlreadyMessage(messageId)) {
          return;
        }

        // The list runs oldest first: the newest message goes last.
        messagesWrapper.insertAdjacentHTML("beforeend", json.data.rendered);
        formatMessageTimes(messagesWrapper);
        scrollElement.scrollTop = scrollElement.scrollHeight;
        // Only the other member's messages: the member's own, sent from another tab
        // or the app, arrive here too.
        announceReceivedMessage(messagesWrapper, messageId);

        // Tell FontAwesome to search for new icons.
        // Fix the bug with reviews icons not being rendered.
        dom.i2svg();
      });
  }

  function hasAlreadyMessage(messageId: number) {
    return messagesWrapper.querySelector(`.message-container[data-id='${messageId}']`);
  }

  // Close connection when user leaves the page
  window.addEventListener(
    "beforeunload",
    () => {
      evtSource.close();
    },
    false,
  );
};
